import React, { ReactNode } from 'react';

import DynamicJsUpdateErrorPage from '#/components/layout/notice/DynamicJsUpdateErrorPage';
import { captureExceptionInPosthog } from '#/lib/analyticsPosthogSetup';

import ErrorPage from './ErrorPage';

export const UNSUPPORTED_BROWSER_TEXT =
  'Your web browser is out of date. Update your browser for more security, speed and the best experience on this site.';

type Props = {
  children: ReactNode;
  displayErrorMessage?: boolean;
};

type State = {
  hasError: boolean;
  /** Handle situation when we update web client and old chunks are no longer served */
  errorType: null | 'dynamicJsLoadFail';
};

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorType: null,
    };
  }

  static getDerivedStateFromError(error: Error) {
    const errorMessage = `${error?.message}`.toLowerCase();

    // Detect stale bundle / dynamic import failures after a deployment.
    // Webpack (legacy): "Loading chunk X failed."
    // Vite (Chrome):    "Failed to fetch dynamically imported module: …"
    // Vite (Firefox):   "error loading dynamically imported module …"
    const isDynamicJsLoadError =
      (errorMessage.includes('loading chunk') &&
        errorMessage.includes('failed')) ||
      errorMessage.includes('failed to fetch dynamically imported module') ||
      errorMessage.includes('error loading dynamically imported module');

    // Update state so the next render will show the fallback UI.
    return {
      hasError: true,
      errorType: isDynamicJsLoadError ? 'dynamicJsLoadFail' : null,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    captureExceptionInPosthog(error, {
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError && this.state.errorType === 'dynamicJsLoadFail') {
      return <DynamicJsUpdateErrorPage />;
    }
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.displayErrorMessage ? (
        <ErrorPage
          title="Oops!"
          text={
            window?.appCheckedModernBrowser
              ? 'Something went wrong...'
              : UNSUPPORTED_BROWSER_TEXT
          }
        />
      ) : null;
    }

    return this.props.children;
  }
}
